// Copyright 2025-2026 Florian Gallon. All Rights reserved.
//
// Weather data provider — the ONLY file that knows about Open-Meteo.
// Uses the free Open-Meteo API (no key, no account). Data: CC BY 4.0, attribution shown in the UI.
// To switch provider (e.g. NASA POWER) or use a paid key, change this file only:
// keep the fetchDailyWeather() signature and the returned row shape.

import { parseLocalDate, toLocalDateString } from './dateUtils';

const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';
const ARCHIVE_URL = 'https://archive-api.open-meteo.com/v1/archive';

// Recent days come from the forecast endpoint, which also serves the past ~92 days and is
// the most reliable host (the archive host lags a few days and can be down — a 502 there shows
// in the browser as a CORS error). Only older days, i.e. the past-days backfill, use the archive.
const RECENT_DAYS = 85;
const MAX_DAYS_PER_CALL = 366;

const DAILY_FIELDS = [
  'weather_code',
  'temperature_2m_max',
  'temperature_2m_min',
  'precipitation_sum',
  'wind_speed_10m_max',
  'sunshine_duration'
].join(',');

export const WEATHER_ATTRIBUTION = {
  label: 'Weather data by Open-Meteo.com',
  url: 'https://open-meteo.com/'
};

const addDays = (dateStr, n) => {
  const d = parseLocalDate(dateStr);
  d.setDate(d.getDate() + n);
  return toLocalDateString(d);
};

const round1 = (v) => (typeof v === 'number' ? Math.round(v * 10) / 10 : null);

async function fetchRange(url, lat, lng, startDate, endDate) {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    start_date: startDate,
    end_date: endDate,
    daily: DAILY_FIELDS,
    timezone: 'auto'
  });
  const response = await fetch(`${url}?${params.toString()}`);
  if (!response.ok) {
    const error = new Error(`Weather service error (${response.status})`);
    error.status = response.status;
    throw error;
  }
  const json = await response.json();
  const daily = json.daily || {};
  return (daily.time || []).map((date, i) => ({
    date,
    weatherCode: daily.weather_code?.[i] ?? null,
    tempMax: round1(daily.temperature_2m_max?.[i]),
    tempMin: round1(daily.temperature_2m_min?.[i]),
    precipitationMm: round1(daily.precipitation_sum?.[i]),
    windMaxKmh: round1(daily.wind_speed_10m_max?.[i]),
    sunshineHours: typeof daily.sunshine_duration?.[i] === 'number'
      ? round1(daily.sunshine_duration[i] / 3600)
      : null
  }));
}

/**
 * Daily weather for a location and an inclusive 'YYYY-MM-DD' range.
 * Future days are dropped; ranges are split between archive / recent and in ≤366-day chunks.
 * Rows with no weather code (data not yet available) are left out.
 * @returns {Promise<Array<{date, weatherCode, tempMax, tempMin, precipitationMm, windMaxKmh, sunshineHours}>>}
 */
export async function fetchDailyWeather({ lat, lng, startDate, endDate }) {
  const today = toLocalDateString(new Date());
  const end = endDate > today ? today : endDate;
  if (!startDate || startDate > end) return [];

  const recentStart = addDays(today, -RECENT_DAYS);
  const segments = [];

  // Archive part: startDate .. min(end, recentStart - 1), chunked
  const archiveEnd = end < recentStart ? end : addDays(recentStart, -1);
  let cursor = startDate;
  while (cursor <= archiveEnd) {
    const chunkEnd = addDays(cursor, MAX_DAYS_PER_CALL - 1);
    const segEnd = chunkEnd < archiveEnd ? chunkEnd : archiveEnd;
    segments.push({ url: ARCHIVE_URL, start: cursor, end: segEnd });
    cursor = addDays(segEnd, 1);
  }

  // Recent part: max(startDate, recentStart) .. end
  const forecastStart = startDate > recentStart ? startDate : recentStart;
  if (forecastStart <= end) {
    segments.push({ url: FORECAST_URL, start: forecastStart, end });
  }

  const rows = [];
  let lastError = null;
  let failed = 0;
  for (const seg of segments) {
    // Sequential on purpose: stays gentle with the free API rate limits.
    // A failing segment (e.g. archive host down) doesn't lose the others.
    try {
      rows.push(...await fetchRange(seg.url, lat, lng, seg.start, seg.end));
    } catch (error) {
      lastError = error;
      failed += 1;
    }
  }
  if (failed > 0 && failed === segments.length) throw lastError;
  return rows.filter(row => row.weatherCode !== null);
}

// Free forecast horizon is today + 16 days; keep one day of margin for time zones
export const FORECAST_MAX_DAYS = 15;

/**
 * Daily FORECAST for a location and an inclusive 'YYYY-MM-DD' range (today … today + 15).
 * Same row shape as fetchDailyWeather. Days outside the horizon are dropped.
 */
export async function fetchDailyForecast({ lat, lng, startDate, endDate }) {
  const today = toLocalDateString(new Date());
  const maxDate = addDays(today, FORECAST_MAX_DAYS);
  const start = startDate < today ? today : startDate;
  const end = endDate > maxDate ? maxDate : endDate;
  if (!start || !end || start > end) return [];
  const rows = await fetchRange(FORECAST_URL, lat, lng, start, end);
  return rows.filter(row => row.weatherCode !== null);
}

/**
 * Geocode a postcode + town + country (ISO-2) to coordinates using OpenStreetMap Nominatim
 * (free, no key). Returns { lat, lng, label } or null when nothing is found.
 */
export async function geocodePostcodeCity({ postalCode, city, country }) {
  const params = new URLSearchParams({ format: 'json', limit: '1', addressdetails: '1' });
  if (postalCode) params.set('postalcode', postalCode.trim());
  if (city) params.set('city', city.trim());
  if (country) params.set('countrycodes', country.toLowerCase());

  let data = [];
  const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
  if (response.ok) data = await response.json();

  // Fallback: Canadian / UK full postcodes are often missing in OSM — retry with the town only
  if ((!data || data.length === 0) && postalCode && city) {
    params.delete('postalcode');
    const retry = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
    if (retry.ok) data = await retry.json();
  }

  if (!data || data.length === 0) return null;
  const hit = data[0];
  const a = hit.address || {};
  const town = a.city || a.town || a.village || a.municipality || city || hit.name;
  const label = [town, a.postcode || postalCode, a.country].filter(Boolean).join(', ');
  return {
    lat: Math.round(parseFloat(hit.lat) * 10000) / 10000,
    lng: Math.round(parseFloat(hit.lon) * 10000) / 10000,
    label
  };
}
